import { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, View } from 'react-native';
import { Icon } from '@/common/components/Icons/Icon';
import { Label, TLabelProps } from '@/common/components/Label/Label';
import { SkeletonInline } from '@/common/components/SkeletonInline/SkeletonInline';
import { Stack } from '@/common/components/Stack/Stack';
import { tv } from '@/common/theme/tv.config';
import { copyToClipboard } from '@/common/utils/copy';
import { TriggerRef } from '@rn-primitives/popover';
import { Popover } from '../Popover/Popover';

export const LabeledValue = ({
  label,
  value,
  className,
  valueTestId,
  isLoading,
  copyable,
  align,
  bold,
  ...props
}: ILabeledValueProps) => {
  const { t } = useTranslation();
  const popoverRef = useRef<TriggerRef>(null);

  const valueAlign =
    align ?? (props.orientation === 'horizontal' ? 'right' : 'left');

  const handleCopy = () => {
    copyToClipboard(typeof value === 'string' ? value : '');
    popoverRef.current?.open();
  };

  const valueWrapper = (
    <Stack horizontal gap="xs" align="center">
      {isLoading ? (
        <SkeletonInline className={styles.skeleton()} />
      ) : (
        <Label
          className={styles.field({ align: valueAlign })}
          size={props.orientation === 'horizontal' ? props.size : undefined}
          bold
          data-test-id={valueTestId}
        >
          {value}
        </Label>
      )}
      {copyable && !isLoading && (
        <Popover
          content={t('general.labels.copied')}
          ref={popoverRef}
          autoCloseDelay={1000}
          onPress={handleCopy}
        >
          <Icon icon={['far', 'copy']} />
        </Popover>
      )}
    </Stack>
  );

  const valueComponent = copyable ? (
    <Pressable onPress={handleCopy}>{valueWrapper}</Pressable>
  ) : (
    valueWrapper
  );

  if (props.orientation === 'horizontal') {
    return (
      <Stack className={className}>
        <Stack horizontal justify="between" align="start">
          <Label size={props.size} bold={bold}>
            {label}
          </Label>
          {props.dashed && <View className={styles.dash()} />}
          {valueComponent}
        </Stack>
        {props.subLabel || props.subValue ? (
          <Stack justify="between" align="center" horizontal>
            <Label color="neutral20" size="sm">
              {props.subLabel}
            </Label>
            {isLoading ? (
              <SkeletonInline />
            ) : (
              <Label bold size="sm" color="neutral20">
                {props.subValue}
              </Label>
            )}
          </Stack>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack className={className} gap="2xs">
      <Label size="sm" color="neutral20" bold={bold}>
        {label}
      </Label>
      {valueComponent}
    </Stack>
  );
};

const styles = tv({
  slots: {
    dash: 'mx-sm min-w-[0.3rem] flex-1 border-b-[0.1rem] border-dashed border-b-neutral20 pt-[1rem]',
    field: 'flex-shrink flex-wrap break-words',
    right: 'text-right',
    skeleton: 'w-[14rem]',
  },
  variants: {
    align: {
      left: {
        field: 'text-left',
      },
      center: {
        field: 'text-center',
      },
      right: {
        field: 'text-right',
      },
    },
  },
})();

export interface ILabeledValueSharedProps {
  label: ReactNode | string;
  value: ReactNode | string;
  className?: string;
  valueTestId?: string;
  isLoading?: boolean;
  copyable?: boolean;
  bold?: boolean;
  align?: 'left' | 'center' | 'right';
}

export interface ILabeledValueHorizontalProps extends ILabeledValueSharedProps {
  orientation: 'horizontal';
  dashed?: boolean;
  subLabel?: ReactNode | string;
  subValue?: ReactNode | string;
  size?: TLabelProps['size'];
}

export interface ILabeledValueVerticalProps extends ILabeledValueSharedProps {
  orientation?: 'vertical';
}

export type ILabeledValueProps =
  | ILabeledValueVerticalProps
  | ILabeledValueHorizontalProps;
